// tabs/PricingTab.js
import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";

function PricingTab({ facilities }) {
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [facilityPricingStatus, setFacilityPricingStatus] = useState({});
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [currentPricing, setCurrentPricing] = useState({
    levy: null,
    lease: null,
    powerMeters: null,
    waterMeters: null,
  });

  useEffect(() => {
    if (selectedFacility) {
      fetchPricing(selectedFacility._id);
    }
  }, [selectedFacility]);

  // Auto-select first facility when component loads
  useEffect(() => {
    if (facilities && facilities.length > 0 && !selectedFacility) {
      setSelectedFacility(facilities[0]);
    }
  }, [facilities, selectedFacility]);

  // Fetch pricing status for all facilities when facilities change
  useEffect(() => {
    if (facilities && facilities.length > 0) {
      fetchAllFacilityPricingStatus();
    }
  }, [facilities]);

  const fetchPricing = async (facilityId) => {
    setLoading(true);
    try {
      const response = await makeRequest2(
        `/api/core/billing/facility/${facilityId}/pricing`,
        "GET",
      );
      if (response.success) {
        const data = response.data;
        if (data && data.pricing) {
          setPricing(data.pricing);
        } else {
          setPricing(null);
        }
      } else {
        // If 404, it means no pricing exists yet
        if (response.error && response.error.includes("not found")) {
          setPricing(null);
        } else {
          toastify("Failed to fetch pricing: " + response.error, "error");
        }
      }
    } catch (error) {
      toastify("Error fetching pricing: " + error.message, "error");
      setPricing(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllFacilityPricingStatus = async () => {
    if (!facilities || facilities.length === 0) return;

    const statusPromises = facilities.map(async (facility) => {
      try {
        const response = await makeRequest2(
          `/api/core/billing/facility/${facility._id}/pricing`,
          "GET",
        );
        return {
          facilityId: facility._id,
          hasPricing:
            response.success && response.data && response.data.pricing,
        };
      } catch (error) {
        return {
          facilityId: facility._id,
          hasPricing: false,
        };
      }
    });

    try {
      const statuses = await Promise.all(statusPromises);
      const statusMap = {};
      statuses.forEach((status) => {
        statusMap[status.facilityId] = status.hasPricing;
      });
      setFacilityPricingStatus(statusMap);
    } catch (error) {
      console.error("Error fetching facility pricing statuses:", error);
    }
  };

  const handleFacilitySelect = (facility) => {
    setSelectedFacility(facility);
    setPricing(null);
  };

  const handleAddPricing = async () => {
    try {
      const response = await makeRequest2(
        `/api/core/billing/facility/${selectedFacility._id}/pricing`,
        "POST",
        currentPricing,
      );
      if (response.success) {
        toastify("Pricing added successfully!", "success");
        fetchPricing(selectedFacility._id);
        fetchAllFacilityPricingStatus(); // Update status for all facilities
        setShowAddDialog(false);
        resetCurrentPricing();
      } else {
        toastify("Failed to add pricing: " + response.error, "error");
      }
    } catch (error) {
      toastify("Error adding pricing: " + error.message, "error");
    }
  };

  const handleEditPricing = async () => {
    try {
      const response = await makeRequest2(
        `/api/core/billing/facility/${selectedFacility._id}/pricing`,
        "PUT",
        currentPricing,
      );
      if (response.success) {
        toastify("Pricing updated successfully!", "success");
        fetchPricing(selectedFacility._id);
        fetchAllFacilityPricingStatus(); // Update status for all facilities
        setShowEditDialog(false);
        resetCurrentPricing();
      } else {
        toastify("Failed to update pricing: " + response.error, "error");
      }
    } catch (error) {
      toastify("Error updating pricing: " + error.message, "error");
    }
  };

  const handleDeletePricing = async () => {
    try {
      const response = await makeRequest2(
        `/api/core/billing/facility/${selectedFacility._id}/pricing`,
        "DELETE",
      );
      if (response.success) {
        toastify("Pricing deleted successfully!", "success");
        setPricing(null);
        fetchAllFacilityPricingStatus(); // Update status for all facilities
        setShowDeleteDialog(false);
      } else {
        toastify("Failed to delete pricing: " + response.error, "error");
      }
    } catch (error) {
      toastify("Error deleting pricing: " + error.message, "error");
    }
  };

  const resetCurrentPricing = () => {
    setCurrentPricing({
      levy: null,
      lease: null,
      powerMeters: null,
      waterMeters: null,
    });
  };

  const openAddDialog = () => {
    resetCurrentPricing();
    setShowAddDialog(true);
  };

  const openEditDialog = () => {
    setCurrentPricing({
      levy: pricing.levy,
      lease: pricing.lease,
      powerMeters: pricing.powerMeters,
      waterMeters: pricing.waterMeters,
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = () => {
    setShowDeleteDialog(true);
  };

  const formatCurrency = (value) => {
    if (value == null) return "-";
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 0,
    }).format(value);
  };

  const renderPricingCard = () => {
    if (!pricing) {
      return (
        <div className="text-center py-5">
          <i className="ti ti-receipt-off display-6 text-muted mb-3 d-block"></i>
          <h6 className="text-muted mb-3">No pricing configuration found</h6>
          <Button
            label="Add Pricing"
            icon="ti ti-plus"
            className="btn btn-primary"
            onClick={openAddDialog}
          />
        </div>
      );
    }

    return (
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h6 className="card-title mb-0">
            <i className="ti ti-receipt me-2"></i>
            Pricing Configuration
          </h6>
          <div className="d-flex gap-2">
            <Button
              icon="ti ti-edit"
              className="btn btn-outline-primary btn-sm"
              onClick={openEditDialog}
              tooltip="Edit Pricing"
            />
            <Button
              icon="ti ti-trash"
              className="btn btn-outline-danger btn-sm"
              onClick={openDeleteDialog}
              tooltip="Delete Pricing"
            />
          </div>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <div className="border rounded p-3">
                <div className="d-flex align-items-center mb-2">
                  <i className="ti ti-cash text-success me-2"></i>
                  <strong>Levy</strong>
                </div>
                <h5 className="mb-0 text-success">
                  {formatCurrency(pricing.levy)}
                </h5>
              </div>
            </div>
            <div className="col-md-6">
              <div className="border rounded p-3">
                <div className="d-flex align-items-center mb-2">
                  <i className="ti ti-home text-primary me-2"></i>
                  <strong>Lease</strong>
                </div>
                <h5 className="mb-0 text-primary">
                  {formatCurrency(pricing.lease)}
                </h5>
              </div>
            </div>
            <div className="col-md-6">
              <div className="border rounded p-3">
                <div className="d-flex align-items-center mb-2">
                  <i className="ti ti-bolt text-warning me-2"></i>
                  <strong>Power Meters</strong>
                </div>
                <h5 className="mb-0 text-warning">
                  {formatCurrency(pricing.powerMeters)}
                </h5>
              </div>
            </div>
            <div className="col-md-6">
              <div className="border rounded p-3">
                <div className="d-flex align-items-center mb-2">
                  <i className="ti ti-droplet text-info me-2"></i>
                  <strong>Water Meters</strong>
                </div>
                <h5 className="mb-0 text-info">
                  {formatCurrency(pricing.waterMeters)}
                </h5>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="row">
      <div className="col-md-4">
        <h5>Facilities</h5>
        <DataTable
          value={facilities || []}
          selectionMode="single"
          onRowClick={(e) => handleFacilitySelect(e.data)}
          paginator
          rows={10}
          emptyMessage="No facilities found"
          loading={loading}
        >
          <Column field="name" header="Facility Name" sortable />
          <Column
            field="_id"
            header="Status"
            body={(rowData) => {
              const hasPricing = facilityPricingStatus[rowData._id];
              return hasPricing ? (
                <span className="badge bg-success">Configured</span>
              ) : (
                <span className="badge bg-warning">Not Configured</span>
              );
            }}
          />
        </DataTable>
      </div>
      <div className="col-md-8">
        {selectedFacility && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>Pricing for {selectedFacility.name}</h5>
            </div>
            {renderPricingCard()}
          </>
        )}
      </div>

      {/* Add Dialog */}
      <Dialog
        visible={showAddDialog}
        onHide={() => setShowAddDialog(false)}
        header="Add Pricing Configuration"
        modal
        style={{ width: "40vw" }}
      >
        <div className="p-fluid">
          <div className="row g-3">
            <div className="col-md-6">
              <div className="field">
                <label>Levy Amount (KES)</label>
                <InputNumber
                  value={currentPricing.levy}
                  onValueChange={(e) =>
                    setCurrentPricing({
                      ...currentPricing,
                      levy: e.target.value,
                    })
                  }
                  mode="currency"
                  currency="KES"
                  locale="en-KE"
                  currencyDisplay="symbol"
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="field">
                <label>Lease Amount (KES)</label>
                <InputNumber
                  value={currentPricing.lease}
                  onValueChange={(e) =>
                    setCurrentPricing({
                      ...currentPricing,
                      lease: e.target.value,
                    })
                  }
                  mode="currency"
                  currency="KES"
                  locale="en-KE"
                  currencyDisplay="symbol"
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="field">
                <label>Power Meters (KES)</label>
                <InputNumber
                  value={currentPricing.powerMeters}
                  onValueChange={(e) =>
                    setCurrentPricing({
                      ...currentPricing,
                      powerMeters: e.target.value,
                    })
                  }
                  mode="currency"
                  currency="KES"
                  locale="en-KE"
                  currencyDisplay="symbol"
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="field">
                <label>Water Meters (KES)</label>
                <InputNumber
                  value={currentPricing.waterMeters}
                  onValueChange={(e) =>
                    setCurrentPricing({
                      ...currentPricing,
                      waterMeters: e.target.value,
                    })
                  }
                  mode="currency"
                  currency="KES"
                  locale="en-KE"
                  currencyDisplay="symbol"
                />
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button
              label="Cancel"
              className="btn btn-outline-secondary"
              onClick={() => setShowAddDialog(false)}
            />
            <Button
              label="Add Pricing"
              className="btn btn-primary"
              onClick={handleAddPricing}
            />
          </div>
        </div>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        visible={showEditDialog}
        onHide={() => setShowEditDialog(false)}
        header="Edit Pricing Configuration"
        modal
        style={{ width: "40vw" }}
      >
        <div className="p-fluid">
          <div className="row g-3">
            <div className="col-md-6">
              <div className="field">
                <label>Levy Amount (KES)</label>
                <InputNumber
                  value={currentPricing.levy}
                  onValueChange={(e) =>
                    setCurrentPricing({
                      ...currentPricing,
                      levy: e.target.value,
                    })
                  }
                  mode="currency"
                  currency="KES"
                  locale="en-KE"
                  currencyDisplay="symbol"
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="field">
                <label>Lease Amount (KES)</label>
                <InputNumber
                  value={currentPricing.lease}
                  onValueChange={(e) =>
                    setCurrentPricing({
                      ...currentPricing,
                      lease: e.target.value,
                    })
                  }
                  mode="currency"
                  currency="KES"
                  locale="en-KE"
                  currencyDisplay="symbol"
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="field">
                <label>Power Meters (KES)</label>
                <InputNumber
                  value={currentPricing.powerMeters}
                  onValueChange={(e) =>
                    setCurrentPricing({
                      ...currentPricing,
                      powerMeters: e.target.value,
                    })
                  }
                  mode="currency"
                  currency="KES"
                  locale="en-KE"
                  currencyDisplay="symbol"
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="field">
                <label>Water Meters (KES)</label>
                <InputNumber
                  value={currentPricing.waterMeters}
                  onValueChange={(e) =>
                    setCurrentPricing({
                      ...currentPricing,
                      waterMeters: e.target.value,
                    })
                  }
                  mode="currency"
                  currency="KES"
                  locale="en-KE"
                  currencyDisplay="symbol"
                />
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button
              label="Cancel"
              className="btn btn-outline-secondary"
              onClick={() => setShowEditDialog(false)}
            />
            <Button
              label="Update Pricing"
              className="btn btn-primary"
              onClick={handleEditPricing}
            />
          </div>
        </div>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog
        visible={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        header="Confirm Delete"
        modal
        style={{ width: "30vw" }}
        footer={
          <div>
            <Button
              label="Cancel"
              className="btn btn-outline-secondary"
              onClick={() => setShowDeleteDialog(false)}
            />
            <Button
              label="Delete"
              className="btn btn-danger"
              onClick={handleDeletePricing}
            />
          </div>
        }
      >
        <div className="d-flex align-items-center">
          <i className="ti ti-alert-triangle text-warning me-3 display-6"></i>
          <div>
            <p className="mb-1">
              <strong>
                Are you sure you want to delete this pricing configuration?
              </strong>
            </p>
            <p className="text-muted mb-0">This action cannot be undone.</p>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

export default PricingTab;
